import { useCallback, useEffect, useRef, useState } from "react";
import { api, errorText } from "../api";
import { copyToClipboard } from "../clipboard";
import { confirmDialog } from "../dialog";
import { num, timeAgo } from "../format";
import { useIsFocused, useTick } from "../hooks";
import { toast } from "../toast";
import type {
  Person,
  Preset,
  StagePresence,
  StageStatus,
  Subscription,
  TuberState,
  Voice,
} from "../types";
import { EmptyState } from "./EmptyState";
import { openMiniWindow } from "./MiniShell";
import { TuberControls } from "./TuberControls";

interface Props {
  volume: number;
  tuber: TuberState | null;
  overlayUrl: string | null;
  /* Per browser source. The card below is about the *main* overlay — the URL it
     shows — so it must not report a count that includes a character's own
     source, or "connected" would be true while the main scene is empty. */
  stages: StageStatus[];
  presets: Preset[];
  mutesEpoch: number;
  onManagePresets: () => void;
  onVoices: (voices: Voice[]) => void;
  onSubscription: (subscription: Subscription) => void;
  onTuber: (tuber: TuberState) => void;
}

const fail = (error: unknown) => toast(errorText(error), "error");

/* Configuration moved out to its own drawer, and the kill switch moved up to
   the header; what is left here is what you reach for while the stream is
   running. */
type Tab = "overlay" | "people" | "controls";

const TABS: { id: Tab; label: string }[] = [
  { id: "overlay", label: "Overlay" },
  { id: "people", label: "People" },
  { id: "controls", label: "Controls" },
];

export function AdminPanel({
  volume,
  tuber,
  overlayUrl,
  stages,
  presets,
  mutesEpoch,
  onManagePresets,
  onVoices,
  onSubscription,
  onTuber,
}: Props) {
  const [tab, setTab] = useState<Tab>("overlay");
  const [volumeFocused, volumeFocus] = useIsFocused();
  const [localVolume, setLocalVolume] = useState(volume);
  const volumeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [people, setPeople] = useState<Person[]>([]);
  const [testing, setTesting] = useState(false);
  const main = stages.find((stage) => stage.stage === null);
  /* Characters with a browser source of their own. Their URLs live in the
     Characters editor; what belongs here is whether OBS is actually showing
     them, because that is the thing you check while the stream is up. */
  const staged = presets.filter((preset) => preset.overlay.own_source);

  // another admin moved it - but never yank the thumb out from under whoever
  // is dragging this panel's own slider
  useEffect(() => {
    if (!volumeFocused) setLocalVolume(volume);
  }, [volume, volumeFocused]);

  useEffect(() => () => clearTimeout(volumeTimer.current), []);

  const loadPeople = useCallback(() => {
    api<{ people: Person[] }>("/api/admin/people")
      .then((body) => setPeople(body.people))
      .catch(fail);
  }, []);

  useEffect(loadPeople, [loadPeople, mutesEpoch]);

  async function setMuted(user: string, muted: boolean) {
    try {
      await api("/api/admin/mute", {
        method: "POST",
        body: JSON.stringify({ user, muted }),
      });
      loadPeople();
    } catch (error) {
      fail(error);
    }
  }

  return (
    <div className="card">
      <h2>Admin</h2>

      <div className="tabs" role="tablist" aria-label="Admin sections">
        {TABS.map((candidate) => (
          <button
            key={candidate.id}
            role="tab"
            aria-selected={tab === candidate.id}
            onClick={() => setTab(candidate.id)}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      {/* Inactive panes are hidden, not unmounted, so a half-dragged slider or
          a scroll position survives a peek at another tab. */}
      <div hidden={tab !== "overlay"}>
        <OverlayHealth
          overlays={main?.connected ?? 0}
          seenAt={main?.seen_at ?? null}
          testing={testing}
          onTest={async () => {
            setTesting(true);
            try {
              await api("/api/admin/overlay/test", { method: "POST" });
              toast("Test tone sent");
            } catch (error) {
              fail(error);
            } finally {
              setTesting(false);
            }
          }}
        />

        {/* Dragging sends while you drag, throttled, so you can dial it in by ear. */}
        <div className="slider" style={{ marginBottom: 0, marginTop: 14 }}>
          <label>
            <span>Stream volume</span>
            <span className="slider-val">{Math.round(localVolume * 100)}%</span>
          </label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={localVolume}
            {...volumeFocus}
            onChange={(event) => {
              const next = parseFloat(event.target.value);
              setLocalVolume(next);
              clearTimeout(volumeTimer.current);
              volumeTimer.current = setTimeout(() => {
                api("/api/admin/volume", {
                  method: "POST",
                  body: JSON.stringify({ volume: next }),
                }).catch(fail);
              }, 120);
            }}
          />

        </div>

        {tuber && <TuberControls tuber={tuber} onState={onTuber} />}

        {/* Every browser source this instance has, main first, each with the
            URL beside the light that says whether OBS is showing it.

            It used to be the main URL here and a character's buried behind
            Manage characters → expand the card → scroll past the voice form,
            which is a long way to go for the one string you paste into OBS
            every time you rebuild a scene. A streamer has a handful of these
            and copies them far more often than they edit them, so the list of
            them is the thing worth putting on the screen you are already on. */}
        <div style={{ marginTop: 14 }}>
          <div className="field-label">OBS browser sources</div>
          <div className="rows" style={{ gap: 10 }}>
            <SourceRow
              name="Main overlay"
              url={overlayUrl}
              presence={main}

            />
            {staged.map((preset) => (
              <SourceRow
                key={preset.id}
                name={preset.name}
                url={preset.overlay_url ?? null}
                presence={stages.find((stage) => stage.stage === preset.id)}
              />
            ))}
          </div>
          <div className="hint">
            <button className="link" onClick={onManagePresets}>
              Manage characters
            </button>
          </div>
        </div>

        {/* The panel itself, as a dock. OBS custom browser docks keep their own
            cookies, so the first open asks for a sign-in like any new browser —
            which is also why the URL carries no secret and is safe to share
            with a mod. */}
        <div style={{ marginTop: 14 }}>
          <div className="field-label">Mini panel</div>

          <div className="row-actions">
            <button className="ghost" onClick={openMiniWindow}>
              Open mini window
            </button>
            <button
              className="ghost"
              onClick={() =>
                void copyToClipboard(`${location.origin}/mini`, "Dock URL copied")
              }
            >
              Copy OBS dock URL
            </button>
          </div>
        </div>
      </div>

      <div hidden={tab !== "people"}>
        <People people={people} onMuted={setMuted} />
      </div>

      <div hidden={tab !== "controls"}>
        <div className="rows" style={{ gap: 8 }}>
          <button
            className="ghost"
            onClick={async () => {
              try {
                onVoices(await api<Voice[]>("/api/voices?refresh=true"));
                onSubscription(await api<Subscription>("/api/subscription"));
                toast("Refreshed", "ok");
              } catch (error) {
                fail(error);
              }
            }}
          >
            Refresh voices &amp; credits
          </button>
          <button
            className="danger"
            onClick={async () => {
              const confirmed = await confirmDialog({
                title: "Purge all history?",
                body: "Deletes all messages and audio. Soundboard clips are kept. This can't be undone.",
                confirmLabel: "Purge",
                danger: true,
              });
              if (!confirmed) return;
              try {
                await api("/api/admin/purge", { method: "POST" });
                toast("History purged", "ok");
              } catch (error) {
                fail(error);
              }
            }}
          >
            Purge all history
          </button>
        </div>

      </div>
    </div>
  );
}

/* One browser source: is it connected, and the string you paste into OBS.

   The copy button is the point of the row — this is what an admin comes to this
   tab for — but the field is readonly-and-selectable behind it, because the
   clipboard is refused often enough that a copy button with no visible URL is a
   dead end rather than a shortcut. */
function SourceRow({
  name,
  url,
  presence,
  note,
}: {
  name: string;
  url: string | null;
  presence: StagePresence | undefined;
  note?: React.ReactNode;
}) {
  const connected = (presence?.connected ?? 0) > 0;

  return (
    <div className="source-row">
      <div className="source-head">
        <span
          className={`dot ${connected ? "on" : "off"}`}
          title={connected ? "Connected in OBS" : "No browser source connected"}
        />
        <b>{name}</b>
        <span className="muted">
          {connected
            ? "connected"
            : presence?.seen_at
              ? `last seen ${timeAgo(presence.seen_at)}`
              : "no source yet"}
        </span>
      </div>
      <div className="field-row">
        <input
          type="text"
          readOnly
          value={url ?? ""}
          onClick={(event) => event.currentTarget.select()}
        />
        <button
          className="ghost"
          disabled={!url}
          onClick={() => void copyToClipboard(url ?? "", `${name} URL copied`)}
        >
          Copy
        </button>
      </div>
      {note && <div className="hint">{note}</div>}
    </div>
  );
}

/* Is the overlay actually there?

   "The overlay isn't working" is the support question this product will get
   most, and it covers three different faults that look identical from the
   panel: no browser source at all, a source that cannot reach the socket, and a
   source whose audio OBS is not capturing. A connection count answers the
   first, the heartbeat answers the second, and the test tone answers the third. */
function OverlayHealth({
  overlays,
  seenAt,
  testing,
  onTest,
}: {
  overlays: number;
  seenAt: number | null;
  testing: boolean;
  onTest: () => void;
}) {
  useTick(); // "12s ago" has to keep counting on its own

  const connected = overlays > 0;
  return (
    <div className={"health" + (connected ? " ok" : "")}>
      <div className="health-line">
        <span className={`dot ${connected ? "on" : "off"}`}></span>
        <b>
          {connected
            ? `${overlays} browser source${overlays === 1 ? "" : "s"} connected`
            : "No overlay connected"}
        </b>
        <span className="spacer"></span>
        <button className="ghost" disabled={!connected || testing} onClick={onTest}>
          {testing ? "Sending…" : "Send test clip"}
        </button>
      </div>
      <div className="hint">
        {connected ? (
          <>Last seen {seenAt ? timeAgo(seenAt) : "just now"}</>
        ) : (
          <>Add the URL below as a browser source in OBS.</>
        )}
      </div>
    </div>
  );
}

/* Who is talking, and what it is costing.

   Moderation used to mean finding somebody's message in the feed and clicking
   the icon on it, which works right up until the person you want is the one who
   has said forty things and scrolled off the top. */
function People({
  people,
  onMuted,
}: {
  people: Person[];
  onMuted: (user: string, muted: boolean) => void;
}) {
  if (people.length === 0) {
    return (
      <EmptyState
        art="people"
        compact
        title="Nobody has spoken yet"
        body="People show up here once they send something."
      />
    );
  }

  return (
    <div className="people">
      {people.map((person) => (
        <div key={person.user} className={"person" + (person.muted ? " muted-row" : "")}>
          <div className="person-top">
            {person.online && <span className="dot on" title="In the panel now"></span>}
            <span className="who">{person.user}</span>
            {person.muted && <span className="badge failed">muted</span>}
            <span className="spacer"></span>
            <button
              className={person.muted ? "ghost" : "ghost danger"}
              onClick={() => onMuted(person.user, !person.muted)}
            >
              {person.muted ? "Unmute" : "Mute"}
            </button>
          </div>
          <div className="meta">
            {`${num(person.messages_today)} today · ${num(person.chars_today)} chars today · ` +
              `${num(person.messages)} all time` +
              (person.failed ? ` · ${num(person.failed)} failed` : "") +
              (person.last_at ? ` · last ${timeAgo(person.last_at)}` : "")}
          </div>
        </div>
      ))}
    </div>
  );
}
